import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useApp } from '@/contexts/AppContext';
import { ReviewCategory } from '@/hooks/useCategories';

interface EditCategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: ReviewCategory | null;
}

export function EditCategoryModal({ open, onOpenChange, category }: EditCategoryModalProps) {
  const { updateCategory } = useApp();
  const [keywords, setKeywords] = useState('');
  const [usp, setUsp] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (category) {
      setKeywords(category.keywords?.join(', ') || '');
      setUsp(category.usp || '');
      setLocation(category.location || '');
    }
  }, [category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!category) return;
    
    setLoading(true);
    const keywordsArray = keywords.trim() 
      ? keywords.split(',').map(k => k.trim()).filter(Boolean) 
      : [];
    
    await updateCategory(category.id, {
      keywords: keywordsArray,
      usp: usp.trim() || undefined,
      location: location.trim() || undefined,
    });
    
    setLoading(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Category: {category?.name}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label className="wireframe-label">Keywords (comma-separated)</Label>
            <Input
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="e.g., friendly staff, quick service, clean facility"
              className="wireframe-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Keywords to highlight in generated reviews
            </p>
          </div>

          <div>
            <Label className="wireframe-label">USP (Unique Selling Proposition)</Label>
            <Textarea
              value={usp}
              onChange={(e) => setUsp(e.target.value)}
              placeholder="e.g., 24/7 availability, award-winning team, latest technology"
              className="wireframe-input min-h-[80px] resize-none"
            />
            <p className="text-xs text-muted-foreground mt-1">
              What makes your service unique for this category
            </p>
          </div>

          <div>
            <Label className="wireframe-label">Location/Branch</Label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., Downtown NYC, Main Street Branch"
              className="wireframe-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Specific location for hyper-local reviews
            </p>
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}